import { Chart, registerables } from 'chart.js';

// Register Chart.js components
Chart.register(...registerables);

// Types
interface SolarRecord {
  time: string;
  value: number;
}

interface DailyData {
  date: string;
  charging: SolarRecord[];
  discharging: SolarRecord[];
  summary: {
    chargingRecords: number;
    dischargingRecords: number;
    totalRecords: number;
    totalCharging: number;
    totalDischarging: number;
  };
}

interface SummaryData {
  processedAt: string;
  totalDates: number;
  dateRange: {
    from: string;
    to: string;
  };
  summary: Array<{
    date: string;
    chargingRecords: number;
    dischargingRecords: number;
    totalRecords: number;
    totalCharging: number;
    totalDischarging: number;
  }>;
}

interface GitHubConfig {
  owner: string;
  repo: string;
  branch: string;
  outputPath: string;
}

class SolarDashboard {
  private summaryData: SummaryData | null = null;
  private currentDayData: DailyData | null = null;
  private energyChart: Chart | null = null;
  private summaryChart: Chart | null = null;
  private githubToken: string | null = null;
  private githubConfig: GitHubConfig = {
    owner: 'Chevindu',
    repo: 'growatt-report-parser',
    branch: 'main',
    outputPath: 'output'
  };

  constructor() {
    this.init();
  }

  async init() {
    // Try to load stored credentials first
    const storedCredentials = this.loadStoredCredentials();
    
    if (storedCredentials.token && storedCredentials.owner && storedCredentials.repo) {
      // Auto-connect with stored credentials
      this.githubToken = storedCredentials.token;
      this.githubConfig.owner = storedCredentials.owner;
      this.githubConfig.repo = storedCredentials.repo;
      this.githubConfig.branch = storedCredentials.branch || 'main';
      this.githubConfig.outputPath = storedCredentials.outputPath || 'output';
      
      this.showLoadingMessage();
      
      try {
        await this.loadSummaryData();
        this.showMainDashboard();
        this.setupEventListeners();
        this.populateDateSelector();
        this.showOverallSummary();
      } catch (error) {
        // If stored credentials fail, show auth form
        this.showAuthenticationForm(true, 'Stored credentials failed: ' + (error as Error).message);
      }
    } else {
      this.showAuthenticationForm();
    }
  }

  private showLoadingMessage() {
    const appDiv = document.getElementById('app')!;
    appDiv.innerHTML = `
      <div class="auth-container">
        <div class="auth-form">
          <h1>🌞 Growatt Solar Dashboard</h1>
          <div class="loading-message">
            <div class="spinner"></div>
            <p>Connecting with saved credentials...</p>
          </div>
        </div>
      </div>
    `;
  }

  private loadStoredCredentials(): any {
    try {
      const stored = localStorage.getItem('growatt-github-config');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to load stored credentials:', error);
      return {};
    }
  }

  private saveCredentials() {
    try {
      const credentials = {
        token: this.githubToken,
        owner: this.githubConfig.owner,
        repo: this.githubConfig.repo,
        branch: this.githubConfig.branch,
        outputPath: this.githubConfig.outputPath,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('growatt-github-config', JSON.stringify(credentials));
    } catch (error) {
      console.warn('Failed to save credentials:', error);
    }
  }

  private clearStoredCredentials() {
    try {
      localStorage.removeItem('growatt-github-config');
    } catch (error) {
      console.warn('Failed to clear stored credentials:', error);
    }
  }

  private showAuthenticationForm(isRetry: boolean = false, errorMessage?: string) {
    const storedCreds = this.loadStoredCredentials();
    const hasStoredCreds = storedCreds.token && storedCreds.owner && storedCreds.repo;
    
    const appDiv = document.getElementById('app')!;
    appDiv.innerHTML = `
      <div class="auth-container">
        <div class="auth-form">
          <h1>🌞 Growatt Solar Dashboard</h1>
          <p>${isRetry ? 'Please update your credentials' : (hasStoredCreds ? 'Update your GitHub configuration' : 'Enter your GitHub token to access solar data')}</p>
          
          ${hasStoredCreds && !isRetry ? `
          <div class="stored-creds-info">
            <p>✅ Found saved credentials for <strong>${storedCreds.owner}/${storedCreds.repo}</strong></p>
            <p>Update details below if needed, or click "Connect with Saved Credentials"</p>
          </div>
          ` : ''}
          
          <div class="form-group">
            <label for="githubToken">GitHub Personal Access Token:</label>
            <input type="password" id="githubToken" placeholder="ghp_xxxxxxxxxxxxxxxxxxxxx" value="${hasStoredCreds ? storedCreds.token : ''}" />
            <small>Token needs 'Contents: Read' permission for the repository</small>
          </div>
          
          <div class="form-group">
            <label for="repoOwner">Repository Owner:</label>
            <input type="text" id="repoOwner" value="${storedCreds.owner || this.githubConfig.owner}" />
          </div>
          
          <div class="form-group">
            <label for="repoName">Repository Name:</label>
            <input type="text" id="repoName" value="${storedCreds.repo || this.githubConfig.repo}" />
          </div>
          
          <div class="form-group">
            <label for="repoBranch">Branch:</label>
            <input type="text" id="repoBranch" value="${storedCreds.branch || this.githubConfig.branch}" />
          </div>
          
          <div class="form-actions">
            ${hasStoredCreds && !isRetry ? `
            <button id="connectSavedBtn" class="connect-btn secondary">Connect with Saved Credentials</button>
            ` : ''}
            <button id="connectBtn" class="connect-btn">Connect to GitHub</button>
            ${hasStoredCreds ? `
            <button id="clearCredsBtn" class="clear-btn">Clear Saved Credentials</button>
            ` : ''}
          </div>
          
          <div id="authError" class="error-message" style="${errorMessage ? 'display: block;' : 'display: none;'}">${errorMessage || ''}</div>
        </div>
      </div>
    `;

    // Event listeners
    document.getElementById('connectBtn')!.addEventListener('click', () => {
      this.authenticateAndLoadData();
    });

    if (hasStoredCreds && !isRetry) {
      document.getElementById('connectSavedBtn')!.addEventListener('click', () => {
        this.connectWithSavedCredentials();
      });
    }

    if (hasStoredCreds) {
      document.getElementById('clearCredsBtn')!.addEventListener('click', () => {
        this.clearStoredCredentials();
        this.showAuthenticationForm(false);
      });
    }

    // Allow enter key to submit
    document.getElementById('githubToken')!.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.authenticateAndLoadData();
      }
    });
  }

  private async authenticateAndLoadData() {
    const tokenInput = document.getElementById('githubToken') as HTMLInputElement;
    const ownerInput = document.getElementById('repoOwner') as HTMLInputElement;
    const repoInput = document.getElementById('repoName') as HTMLInputElement;
    const branchInput = document.getElementById('repoBranch') as HTMLInputElement;
    const errorDiv = document.getElementById('authError')!;
    const connectBtn = document.getElementById('connectBtn') as HTMLButtonElement;

    this.githubToken = tokenInput.value.trim();
    this.githubConfig.owner = ownerInput.value.trim();
    this.githubConfig.repo = repoInput.value.trim();
    this.githubConfig.branch = branchInput.value.trim() || 'main';

    if (!this.githubToken) {
      this.showAuthError('Please enter a GitHub token');
      return;
    }

    connectBtn.textContent = 'Connecting...';
    connectBtn.disabled = true;
    errorDiv.style.display = 'none';

    try {
      await this.loadSummaryData();
      // Save credentials only after successful connection
      this.saveCredentials();
      this.showMainDashboard();
      this.setupEventListeners();
      this.populateDateSelector();
      this.showOverallSummary();
    } catch (error) {
      this.showAuthError('Failed to connect: ' + (error as Error).message);
      connectBtn.textContent = 'Connect to GitHub';
      connectBtn.disabled = false;
    }
  }

  private showAuthError(message: string) {
    const errorDiv = document.getElementById('authError')!;
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
  }

  private showMainDashboard() {
    const appDiv = document.getElementById('app')!;
    appDiv.innerHTML = `
      <header>
        <h1>🌞 Growatt Solar Storage Dashboard</h1>
        <p>Visualize your solar energy storage data</p>
        <button id="reconnectBtn" class="reconnect-btn">Change Repository</button>
      </header>
      
      <main>
        <div class="controls">
          <div class="date-selector">
            <label for="dateSelect">Select Date for Detailed View:</label>
            <select id="dateSelect">
              <option value="">Loading dates...</option>
            </select>
          </div>
          <div class="summary-cards" id="summaryCards">
            <!-- Summary cards will be populated dynamically -->
          </div>
        </div>
        
        <div class="charts-container">
          <div class="chart-section">
            <h3 id="chartTitle">Overall Energy Summary</h3>
            <canvas id="energyChart"></canvas>
          </div>
          
          <div class="chart-section" id="summaryChartSection">
            <h3>Daily Energy Comparison</h3>
            <canvas id="summaryChart"></canvas>
          </div>
        </div>
        
        <div class="data-table-container" id="dataTableContainer" style="display: none;">
          <h3>Raw Data</h3>
          <div class="table-wrapper">
            <table id="dataTable">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Value (kWh)</th>
                </tr>
              </thead>
              <tbody id="dataTableBody">
              </tbody>
            </table>
          </div>
        </div>
      </main>
    `;

    document.getElementById('reconnectBtn')!.addEventListener('click', () => {
      this.showAuthenticationForm();
    });
  }

  private async fetchFromGitHub(path: string): Promise<any> {
    const url = `https://api.github.com/repos/${this.githubConfig.owner}/${this.githubConfig.repo}/contents/${path}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `token ${this.githubToken}`,
        'Accept': 'application/vnd.github.v3+json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid GitHub token or insufficient permissions');
      } else if (response.status === 404) {
        throw new Error(`File not found: ${path}`);
      }
      throw new Error(`GitHub API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.content) {
      // Decode base64 content
      const content = atob(data.content.replace(/\s/g, ''));
      return JSON.parse(content);
    }
    
    throw new Error('No content found in response');
  }

  private async loadSummaryData() {
    try {
      this.summaryData = await this.fetchFromGitHub(`${this.githubConfig.outputPath}/summary.json`);
    } catch (error) {
      throw new Error('Failed to load summary data from GitHub. Make sure the file exists and your token has access.');
    }
  }

  private async loadDayData(date: string): Promise<DailyData> {
    try {
      return await this.fetchFromGitHub(`${this.githubConfig.outputPath}/solar_data_${date}.json`);
    } catch (error) {
      throw new Error(`Failed to load data for ${date} from GitHub`);
    }
  }

  private setupEventListeners() {
    const dateSelect = document.getElementById('dateSelect') as HTMLSelectElement;
    dateSelect.addEventListener('change', async (e) => {
      const target = e.target as HTMLSelectElement;
      if (target.value) {
        await this.loadAndDisplayDayData(target.value);
      } else {
        // Show summary view when no date is selected
        this.showOverallSummary();
        this.hideDetailedView();
      }
    });
  }

  private hideDetailedView() {
    const dataTableContainer = document.getElementById('dataTableContainer')!;
    dataTableContainer.style.display = 'none';
    
    // Hide the energy chart section when showing overview
    const energyChartSection = document.querySelector('.chart-section') as HTMLElement;
    if (energyChartSection) {
      energyChartSection.style.display = 'none';
    }
    
    const chartTitle = document.getElementById('chartTitle')!;
    chartTitle.textContent = 'Overview - Select a date for detailed analysis';
  }

  private populateDateSelector() {
    const dateSelect = document.getElementById('dateSelect') as HTMLSelectElement;
    
    if (!this.summaryData) {
      dateSelect.innerHTML = '<option value="">No data available</option>';
      return;
    }

    dateSelect.innerHTML = '<option value="">Select a date...</option>';
    
    this.summaryData.summary
      .sort((a, b) => b.date.localeCompare(a.date)) // Sort newest first
      .forEach(day => {
        const option = document.createElement('option');
        option.value = day.date;
        option.textContent = `${day.date} (${day.totalRecords} records)`;
        dateSelect.appendChild(option);
      });
  }

  private showOverallSummary() {
    if (!this.summaryData) return;

    const summaryCards = document.getElementById('summaryCards')!;
    
    const totalCharging = this.summaryData.summary.reduce((sum, day) => sum + day.totalCharging, 0);
    const totalDischarging = this.summaryData.summary.reduce((sum, day) => sum + day.totalDischarging, 0);
    const totalRecords = this.summaryData.summary.reduce((sum, day) => sum + day.totalRecords, 0);

    summaryCards.innerHTML = `
      <div class="summary-card">
        <h4>Total Days</h4>
        <div class="value">${this.summaryData.totalDates}</div>
        <div class="unit">days</div>
      </div>
      <div class="summary-card">
        <h4>Total Records</h4>
        <div class="value">${totalRecords.toLocaleString()}</div>
        <div class="unit">data points</div>
      </div>
      <div class="summary-card charging">
        <h4>Total Charging</h4>
        <div class="value">${totalCharging.toFixed(2)}</div>
        <div class="unit">kWh</div>
      </div>
      <div class="summary-card discharging">
        <h4>Total Discharging</h4>
        <div class="value">${totalDischarging.toFixed(2)}</div>
        <div class="unit">kWh</div>
      </div>
    `;

    // Hide the energy chart section in overview mode since it will be empty
    const energyChartSection = document.querySelector('.chart-section') as HTMLElement;
    if (energyChartSection) {
      energyChartSection.style.display = 'none';
    }

    this.createOverallSummaryChart();
  }

  private createOverallSummaryChart() {
    if (!this.summaryData) return;

    const ctx = document.getElementById('summaryChart') as HTMLCanvasElement;
    
    if (this.summaryChart) {
      this.summaryChart.destroy();
    }

    const dates = this.summaryData.summary
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(day => day.date);
    
    const chargingData = this.summaryData.summary
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(day => day.totalCharging);
    
    const dischargingData = this.summaryData.summary
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(day => day.totalDischarging);

    this.summaryChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [
          {
            label: 'Daily Charging (kWh)',
            data: chargingData,
            borderColor: '#4facfe',
            backgroundColor: 'rgba(79, 172, 254, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'Daily Discharging (kWh)',
            data: dischargingData,
            borderColor: '#fa709a',
            backgroundColor: 'rgba(250, 112, 154, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Daily Energy Summary Over Time'
          },
          legend: {
            position: 'top' as const
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Energy (kWh)'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Date'
            }
          }
        }
      }
    });
  }

  private async loadAndDisplayDayData(date: string) {
    const chartTitle = document.getElementById('chartTitle')!;
    chartTitle.textContent = 'Loading...';
    
    try {
      this.currentDayData = await this.loadDayData(date);
      chartTitle.textContent = `Energy Flow - ${date}`;
      this.updateSummaryCards();
      this.createEnergyChart();
      this.populateDataTable();
      this.showDetailedView();
    } catch (error) {
      chartTitle.textContent = 'Error Loading Data';
      this.showError('Failed to load data for ' + date + ': ' + (error as Error).message);
    }
  }

  private showDetailedView() {
    const dataTableContainer = document.getElementById('dataTableContainer')!;
    dataTableContainer.style.display = 'block';
    
    // Show the energy chart section when displaying detailed data
    const energyChartSection = document.querySelector('.chart-section') as HTMLElement;
    if (energyChartSection) {
      energyChartSection.style.display = 'block';
    }
  }

  private updateSummaryCards() {
    if (!this.currentDayData) return;

    const summaryCards = document.getElementById('summaryCards')!;
    
    summaryCards.innerHTML = `
      <div class="summary-card">
        <h4>${this.currentDayData.date}</h4>
        <div class="value">${this.currentDayData.summary.totalRecords}</div>
        <div class="unit">total records</div>
      </div>
      <div class="summary-card charging">
        <h4>Charging</h4>
        <div class="value">${this.currentDayData.summary.totalCharging.toFixed(2)}</div>
        <div class="unit">kWh (${this.currentDayData.summary.chargingRecords} records)</div>
      </div>
      <div class="summary-card discharging">
        <h4>Discharging</h4>
        <div class="value">${this.currentDayData.summary.totalDischarging.toFixed(2)}</div>
        <div class="unit">kWh (${this.currentDayData.summary.dischargingRecords} records)</div>
      </div>
      <div class="summary-card">
        <h4>Net Energy</h4>
        <div class="value">${(this.currentDayData.summary.totalCharging - this.currentDayData.summary.totalDischarging).toFixed(2)}</div>
        <div class="unit">kWh</div>
      </div>
    `;
  }

  private createEnergyChart() {
    if (!this.currentDayData) return;

    const ctx = document.getElementById('energyChart') as HTMLCanvasElement;
    
    if (this.energyChart) {
      this.energyChart.destroy();
    }

    // Combine and sort all data points
    const allData = [
      ...this.currentDayData.charging.map(record => ({ ...record, type: 'charging' })),
      ...this.currentDayData.discharging.map(record => ({ ...record, type: 'discharging' }))
    ].sort((a, b) => a.time.localeCompare(b.time));

    const labels = allData.map(record => record.time);
    const chargingData = allData.map(record => record.type === 'charging' ? record.value : null);
    const dischargingData = allData.map(record => record.type === 'discharging' ? record.value : null);

    this.energyChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Charging (kWh)',
            data: chargingData,
            borderColor: '#4facfe',
            backgroundColor: 'rgba(79, 172, 254, 0.1)',
            pointBackgroundColor: '#4facfe',
            pointBorderColor: '#4facfe',
            tension: 0.4,
            spanGaps: true
          },
          {
            label: 'Discharging (kWh)',
            data: dischargingData,
            borderColor: '#fa709a',
            backgroundColor: 'rgba(250, 112, 154, 0.1)',
            pointBackgroundColor: '#fa709a',
            pointBorderColor: '#fa709a',
            tension: 0.4,
            spanGaps: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: `Energy Flow for ${this.currentDayData.date}`
          },
          legend: {
            position: 'top' as const
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Energy (kWh)'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Time'
            }
          }
        }
      }
    });
  }

  private populateDataTable() {
    if (!this.currentDayData) return;

    const tableBody = document.querySelector('#dataTable tbody')!;
    
    // Combine and sort all data points
    const allData = [
      ...this.currentDayData.charging.map(record => ({ ...record, type: 'Charging' })),
      ...this.currentDayData.discharging.map(record => ({ ...record, type: 'Discharging' }))
    ].sort((a, b) => a.time.localeCompare(b.time));

    tableBody.innerHTML = allData
      .map(record => `
        <tr class="${record.type.toLowerCase()}-row">
          <td>${record.time}</td>
          <td>${record.type}</td>
          <td>${record.value.toFixed(4)}</td>
        </tr>
      `)
      .join('');
  }

  private showError(message: string) {
    const summaryCards = document.getElementById('summaryCards')!;
    summaryCards.innerHTML = `<div class="error">${message}</div>`;
  }

  private async connectWithSavedCredentials() {
    const storedCreds = this.loadStoredCredentials();
    
    this.githubToken = storedCreds.token;
    this.githubConfig.owner = storedCreds.owner;
    this.githubConfig.repo = storedCreds.repo;
    this.githubConfig.branch = storedCreds.branch || 'main';
    this.githubConfig.outputPath = storedCreds.outputPath || 'output';
    
    this.showLoadingMessage();
    
    try {
      await this.loadSummaryData();
      this.showMainDashboard();
      this.setupEventListeners();
      this.populateDateSelector();
      this.showOverallSummary();
    } catch (error) {
      this.showAuthenticationForm(true, 'Saved credentials failed: ' + (error as Error).message);
    }
  }
}

// Initialize the dashboard when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new SolarDashboard();
});
